import * as React from "react";
import { FormProvider, useWatch } from "react-hook-form";

import {
  submitError,
  type SubmitError,
} from "@/shared/components/ResourceForm";
import {
  TriggerSourceType,
  type CreateTriggerV2Request,
  type TriggerV2Record,
} from "@/shared/domain/triggers";

import { buildTriggerRequest } from "./buildTriggerRequest";
import { FormActions } from "./sections/FormActions";
import { NameSection } from "./sections/NameSection";
import { RepeatsSection } from "./sections/RepeatsSection";
import { TargetsSection } from "./sections/TargetsSection";
import { TriggerPreview } from "./sections/TriggerPreview";
import { WhenSection } from "./sections/WhenSection";
import { StoredTriggerContext } from "./storedTriggerContext";
import styles from "./TriggerForm.module.css";
import { type TriggerFormValues } from "./triggerFormSchema";
import { useTriggerForm } from "./useTriggerForm";

export type TriggerFormProps = (
  | { mode: "create" }
  /** Seeded from the stored trigger; the name shows filled and read-only. */
  | { mode: "edit"; initial: TriggerV2Record }
) & {
  /**
   * Receives the built request and resolves when the page's mutation settles.
   * The pending promise is the submitting state and a rejection becomes the
   * form's own server error, so neither is a separate prop.
   */
  onSubmit: (request: CreateTriggerV2Request) => Promise<void>;
  onCancel?: () => void;
};

/** The form's sections. A schedule has no repeats section. */
function Sections({ mode }: { mode: "create" | "edit" }) {
  const firesOn = useWatch<TriggerFormValues, "firesOn">({ name: "firesOn" });

  return (
    <>
      <WhenSection />
      {firesOn.type === TriggerSourceType.Events && (
        <RepeatsSection subscription={firesOn} />
      )}
      <TargetsSection />
      <NameSection mode={mode} />
    </>
  );
}

/**
 * The whole create-or-edit trigger form: one react-hook-form form over one
 * schema, laid out as the page a person reads top to bottom.
 *
 * Every section below reads and writes the form itself through
 * `useFormContext`, so nothing here hands values down.
 */
export function TriggerForm(props: TriggerFormProps) {
  const { onSubmit, onCancel } = props;
  const form = useTriggerForm(
    props.mode === "edit"
      ? { mode: "edit", initial: props.initial }
      : { mode: "create" },
  );

  // `handleSubmit` re-throws whatever the handler threw, so the page's refusal
  // is caught around the call rather than inside the handler.
  const submit = form.handleSubmit(async (values) => {
    await onSubmit(buildTriggerRequest(values));
  });

  // The refusal is the page's answer, not a field's, and it is held here
  // rather than under the form's `root` error: any change after the first
  // submit re-validates the whole form, and that replaces every error the
  // schema did not produce.
  const [serverError, setServerError] = React.useState<SubmitError | null>(
    null,
  );
  const storedTriggerId =
    props.mode === "edit" ? props.initial.trigger_id : null;
  const storedTriggerOrgId =
    props.mode === "edit" ? props.initial.org_id : null;
  const storedTrigger = React.useMemo(
    () =>
      storedTriggerId === null || storedTriggerOrgId === null
        ? null
        : { triggerId: storedTriggerId, orgId: storedTriggerOrgId },
    [storedTriggerId, storedTriggerOrgId],
  );

  return (
    <StoredTriggerContext.Provider value={storedTrigger}>
      <FormProvider {...form}>
        <form
          className={styles.form}
          data-testid="trigger-form"
          onSubmit={(event) => {
            // Every attempt starts without the last one's refusal, so a stale
            // message never sits beside a form the person has since fixed.
            setServerError(null);
            submit(event).catch((error: unknown) => {
              setServerError(submitError(error, props.mode, "trigger"));
            });
          }}
          noValidate
        >
          {/* Three areas rather than a preview nested in the sections: the grid
            is what decides whether the preview rides alongside them or falls
            between them and the submit, so it stays one element either way. */}
          <div className={styles.layout} data-testid="trigger-form-layout">
            <div className={styles.main} data-testid="trigger-form-main">
              <Sections mode={props.mode} />
            </div>
            <div className={styles.preview}>
              <TriggerPreview />
            </div>
            <div className={styles.actions}>
              <FormActions
                mode={props.mode}
                onCancel={onCancel}
                serverError={serverError}
              />
            </div>
          </div>
        </form>
      </FormProvider>
    </StoredTriggerContext.Provider>
  );
}
